import { useDispatch, useSelector } from "react-redux";
import { useState, useEffect } from "react";
import { unwrapResult } from "@reduxjs/toolkit";
import { addAlert, selectLoading } from "src/modules/notification/redux";
import {
  reassignNode,
  getNodesWithWhiteLabel,
} from "src/modules/companies/redux";
import NodeReassignPanelComponent from "./nodeReassignPanel.component";

export default function NodeReassignPanelContainer({
  selectedCompany,
  onSerialNumberAssignDialogCloseClick,
}) {
  const dispatch = useDispatch();

  const isSubmitting = useSelector((state) =>
    selectLoading(state, reassignNode.typePrefix)
  );

  const [sourceNodeUuid, setSourceNodeUuid] = useState("");
  const [targetNodeUuid, setTargetNodeUuid] = useState("");
  const [mode, setMode] = useState(1);
  const [fromValue, setFromValue] = useState("");
  const [toValue, setToValue] = useState("");
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [nodes, setNodes] = useState([]);

  useEffect(() => {
    if (selectedCompany?.company?.id) {
      dispatch(getNodesWithWhiteLabel({
        id: selectedCompany.company.id,
        page: 0,
        length: 1000,
      }))
        .then(unwrapResult)
        .then((result) => {
          setNodes(result?.data || []);
        })
        .catch(() => {});
    }
  }, [dispatch, selectedCompany]);

  const handleModeChange = (event) => {
    setMode(Number(event.target.value));
    setFromValue("");
    setToValue("");
  };

  const handleSubmit = () => {
    if (!sourceNodeUuid || !targetNodeUuid || !fromValue || !toValue) {
      dispatch(
        addAlert({
          severity: "warning",
          message: "Please fill in all fields.",
        })
      );
      return;
    }
    setShowConfirmDialog(true);
  };

  const handleCancelDialog = () => setShowConfirmDialog(false);

  const handleConfirmReassign = async () => {
    setShowConfirmDialog(false);

    const payload = {
      source_node_uuid: sourceNodeUuid,
      target_node_uuid: targetNodeUuid,
      mode,
    };

    if (mode === 1) {
      payload.from_seq_num = Number(fromValue);
      payload.to_seq_num = Number(toValue);
    } else {
      payload.from_sn = fromValue;
      payload.to_sn = toValue;
    }

    try {
      const action = await dispatch(reassignNode(payload));
      const result = unwrapResult(action);
      dispatch(
        addAlert({
          severity: "success",
          message: result?.message || "Node reassignment completed successfully.",
        })
      );
      setSourceNodeUuid("");
      setTargetNodeUuid("");
      setFromValue("");
      setToValue("");
    } catch (error) {
      dispatch(
        addAlert({
          severity: "error",
          message: error?.message || "Failed to reassign node.",
        })
      );
    }
  };

  const isSubmitDisabled =
    !sourceNodeUuid ||
    !targetNodeUuid ||
    !fromValue ||
    !toValue ||
    sourceNodeUuid === targetNodeUuid ||
    isSubmitting;

  return (
    <NodeReassignPanelComponent
      nodes={nodes}
      sourceNodeUuid={sourceNodeUuid}
      targetNodeUuid={targetNodeUuid}
      mode={mode}
      fromValue={fromValue}
      toValue={toValue}
      isSubmitting={isSubmitting}
      isSubmitDisabled={isSubmitDisabled}
      showConfirmDialog={showConfirmDialog}
      setSourceNodeUuid={setSourceNodeUuid}
      setTargetNodeUuid={setTargetNodeUuid}
      handleModeChange={handleModeChange}
      setFromValue={setFromValue}
      setToValue={setToValue}
      handleSubmit={handleSubmit}
      handleConfirmReassign={handleConfirmReassign}
      handleCancelDialog={handleCancelDialog}
      onSerialNumberAssignDialogCloseClick={onSerialNumberAssignDialogCloseClick}
    />
  );
}
